import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  AlertCircle, AlertTriangle, ArrowLeft, CheckCircle2, CheckSquare, Copy,
  Download, RefreshCw, ShieldAlert, Search, Square, X, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { cn } from '../lib/utils';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { AssignmentPicker } from './copyProducts/AssignmentPicker';
import { CostCopySection } from './copyProducts/CostCopySection';
import { ApplyResultPanel } from './copyProducts/ApplyResultPanel';
import { DestSessionChooser } from './copyProducts/DestSessionChooser';

const API = getBackendUrl();
function authHeader() {
  const token = localStorage.getItem('access_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// ── Searchable site combobox ──────────────────────────────────────────────────
function SiteCombobox({ sites, value, onValueChange, placeholder, disabled, testId }) {
  const [open, setOpen] = useState(false);
  const selectedSite = sites.find(s => String(s.id) === String(value));
  const displayLabel = selectedSite
    ? (selectedSite.client_name
        ? `${selectedSite.client_name} / ${selectedSite.name}`
        : selectedSite.name)
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal h-9 text-left"
          data-testid={testId}
        >
          <span className="truncate">
            {displayLabel || <span className="text-slate-400">{placeholder}</span>}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by client or site name…" />
          <CommandList>
            <CommandEmpty>No sites found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => { onValueChange('__none__'); setOpen(false); }}
                className="cursor-pointer text-slate-400 italic"
              >
                — choose source site —
              </CommandItem>
              {sites.map(s => (
                <CommandItem
                  key={s.id}
                  value={`${s.client_name || ''} ${s.name}`}
                  onSelect={() => { onValueChange(String(s.id)); setOpen(false); }}
                  className="cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    {s.client_name ? (
                      <span className="text-sm text-slate-700 truncate">
                        <span className="text-slate-400">{s.client_name}</span>
                        <span className="text-slate-300 mx-1">/</span>
                        {s.name}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-800 truncate">{s.name}</span>
                    )}
                  </div>
                  <span className="ml-auto text-xs text-slate-400 shrink-0 pl-2">{s.product_count}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Measure detail collapse ───────────────────────────────────────────────────
function DetailCollapse({ title, items, colorClass = 'text-slate-700', accent }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  return (
    <div className="text-xs">
      <button
        className={`flex items-center gap-1 font-medium ${colorClass} hover:underline`}
        onClick={() => setOpen(v => !v)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {title} ({items.length})
      </button>
      {open && (
        <div className="mt-1 max-h-72 overflow-y-auto space-y-1 pl-4 border-l border-slate-200">
          {items.map((item, i) => (
            <div key={i} className="flex flex-col gap-0.5 py-0.5 border-b border-slate-100 last:border-0">
              <span className="font-medium text-slate-700 truncate" title={item.product_name}>
                {item.product_code && <span className="font-mono text-slate-400 mr-1">{item.product_code}</span>}
                {item.product_name}
              </span>
              {accent && accent(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DestPreviewCard({ d, opts, assignSel, onAssign, costSel, onCost }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid={`copy-dest-preview-${d.dest_site_id}`}>
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-100">
        <span className="text-sm font-medium text-slate-700">{d.dest_site_name}</span>
        <div className="flex flex-wrap gap-1.5">
          {d.additions_count > 0 && (
            <Badge className="bg-green-100 text-green-700 text-xs border-green-200">+{d.additions_count} assignments</Badge>
          )}
          {d.already_assigned_count > 0 && (
            <Badge variant="secondary" className="text-xs">{d.already_assigned_count} existing</Badge>
          )}
          {opts.include_measures && d.measures && d.measures.count_to_copy > 0 && (
            <Badge className="bg-sky-100 text-sky-700 text-xs border-sky-200">+{d.measures.count_to_copy} measures</Badge>
          )}
          {opts.include_measures && d.measures && opts.overwrite_measures && d.measures.count_conflicts > 0 && (
            <Badge className="bg-amber-100 text-amber-700 text-xs border-amber-200">{d.measures.count_conflicts} measure conflicts → overwrite</Badge>
          )}
          {opts.include_prices && d.prices && d.prices.count_to_copy > 0 && (
            <Badge className="bg-violet-100 text-violet-700 text-xs border-violet-200">+{d.prices.count_to_copy} prices</Badge>
          )}
          {opts.include_prices && d.prices && d.prices.count_blocked > 0 && (
            <Badge className="bg-red-100 text-red-700 text-xs border-red-200">{d.prices.count_blocked} currency-blocked</Badge>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Assignments to add */}
        <AssignmentPicker
          destId={d.dest_site_id}
          items={d.additions}
          selected={assignSel}
          onToggle={pid => onAssign(pid)}
          onSetAll={ids => onAssign(null, ids)}
        />

        {/* Measures section */}
        {opts.include_measures && d.measures && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Measure Overrides</p>
            <div className="grid grid-cols-2 gap-x-4 text-xs text-slate-500">
              <span>To copy: <strong className="text-sky-700">{d.measures.count_to_copy}</strong></span>
              <span>Conflicts: <strong className={d.measures.count_conflicts > 0 ? 'text-amber-700' : 'text-slate-400'}>{d.measures.count_conflicts}</strong></span>
              <span>Identical (skip): <strong className="text-slate-400">{d.measures.count_identical}</strong></span>
              <span>No source value: <strong className="text-slate-400">{d.measures.count_missing}</strong></span>
            </div>
            <DetailCollapse
              title="Measures to copy"
              items={d.measures.to_copy}
              colorClass="text-sky-700"
              accent={item => item.value && (
                <span className="text-slate-500">
                  {item.value.measure_label || ''}{item.value.measure_ml ? ` (${item.value.measure_ml}ml)` : ''}
                </span>
              )}
            />
            <DetailCollapse
              title={opts.overwrite_measures ? "Conflicts → will overwrite" : "Conflicts (preserved — overwrite not selected)"}
              items={d.measures.conflicts}
              colorClass={opts.overwrite_measures ? "text-amber-700" : "text-slate-500"}
              accent={item => (
                <span className="text-slate-400">
                  Dest: {item.destination?.measure_label || ''}{item.destination?.measure_ml ? ` (${item.destination.measure_ml}ml)` : ''}{' → '}
                  Src: {item.source?.measure_label || ''}{item.source?.measure_ml ? ` (${item.source.measure_ml}ml)` : ''}
                </span>
              )}
            />
          </div>
        )}

        {/* Prices section */}
        {opts.include_prices && d.prices && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Bar Prices
              {d.prices.source_currency !== d.prices.dest_currency && (
                <span className="ml-2 text-red-600 normal-case font-normal">
                  ⚠ Currency mismatch ({d.prices.source_currency} → {d.prices.dest_currency}) — all prices blocked
                </span>
              )}
            </p>
            {d.prices.count_blocked > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {d.prices.count_blocked} price{d.prices.count_blocked !== 1 ? 's' : ''} blocked — source site uses{' '}
                {d.prices.source_currency}, destination uses {d.prices.dest_currency}.
                Prices cannot be copied across different currencies.
              </div>
            )}
            {d.prices.count_blocked === 0 && (
              <div className="grid grid-cols-2 gap-x-4 text-xs text-slate-500">
                <span>To copy (new row): <strong className="text-violet-700">{d.prices.count_to_copy}</strong></span>
                <span>Conflicts: <strong className={d.prices.count_conflicts > 0 ? 'text-amber-700' : 'text-slate-400'}>{d.prices.count_conflicts}</strong></span>
                <span>Identical (skip): <strong className="text-slate-400">{d.prices.count_identical}</strong></span>
                <span>No source price: <strong className="text-slate-400">{d.prices.count_missing}</strong></span>
              </div>
            )}
            <DetailCollapse
              title="Prices to copy (effective today)"
              items={d.prices.to_copy}
              colorClass="text-violet-700"
              accent={item => (
                <span className="text-slate-500">
                  £{item.source_price?.toFixed(2)} (src effective {item.source_effective_from})
                </span>
              )}
            />
            <DetailCollapse
              title={opts.overwrite_prices ? "Price conflicts → new effective-today row" : "Price conflicts (preserved — overwrite not selected)"}
              items={d.prices.conflicts}
              colorClass={opts.overwrite_prices ? "text-amber-700" : "text-slate-500"}
              accent={item => (
                <span className="text-slate-400">
                  Dest: £{item.dest_price?.toFixed(2)} → Src: £{item.source_price?.toFixed(2)}
                </span>
              )}
            />
          </div>
        )}

        {opts.include_costs && d.costs && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Opening-Stock Costs</p>
            <CostCopySection
              costs={d.costs}
              selected={costSel}
              onToggle={pid => onCost(pid)}
              onSetAll={ids => onCost(null, ids)}
              overwrite={opts.overwrite_costs}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CopyAssignedProductsPage() {
  useAuth();
  const navigate = useNavigate();

  const [sites, setSites] = useState([]);
  const [sourceSite, setSourceSite] = useState('__none__');
  const [destSites, setDestSites] = useState([]);
  const [destSearch, setDestSearch] = useState('');

  // copy options
  const [includeMeasures, setIncludeMeasures] = useState(false);
  const [includePrices, setIncludePrices] = useState(false);
  const [overwriteMeasures, setOverwriteMeasures] = useState(false);
  const [overwritePrices, setOverwritePrices] = useState(false);
  // Cost-copy — default OFF per spec.
  const [includeCosts, setIncludeCosts] = useState(false);
  const [overwriteCosts, setOverwriteCosts] = useState(false);

  const [preview, setPreview] = useState(null);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);

  // Explicit per-destination selections: { destSiteId (string): Set<productId> }
  const [assignSel, setAssignSel] = useState({});
  const [costSel, setCostSel] = useState({});
  const [destSessions, setDestSessions] = useState({});
  const [costSessionIds, setCostSessionIds] = useState({});
  const [applyResult, setApplyResult] = useState(null);
  const setCostExclusions = () => setCostSel({});

  const selUpdater = (setter) => (destSiteId, pid, ids) => setter(prev => {
    const key = String(destSiteId);
    if (ids) return { ...prev, [key]: new Set(ids) };
    const cur = new Set(prev[key] || []);
    if (cur.has(pid)) cur.delete(pid); else cur.add(pid);
    return { ...prev, [key]: cur };
  });
  const updateAssign = selUpdater(setAssignSel);
  const updateCost = selUpdater(setCostSel);

  const loadDestSessions = () => {
    if (!includeCosts || destSites.length === 0) return;
    fetch(`${API}/api/admin/site-copy/dest-sessions?site_ids=${destSites.join(',')}`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : {})
      .then(setDestSessions)
      .catch(() => toast.error('Failed to load destination sessions'));
  };
  useEffect(loadDestSessions, [includeCosts, destSites]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionIdMap = () => Object.fromEntries(
    destSites.filter(id => costSessionIds[id]).map(id => [id, Number(costSessionIds[id])]));

  useEffect(() => {
    fetch(`${API}/api/admin/site-copy/sites`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setSites(Array.isArray(d) ? d : []))
      .catch(() => toast.error('Failed to load sites'));
    loadRuns();
  }, []);

  const handleExport = async (runId) => {
    try {
      const r = await fetch(`${API}/api/admin/site-copy/runs/${runId}/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (!r.ok) { toast.error('Export failed'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `site_copy_${runId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const loadRuns = async () => {
    setLoadingRuns(true);
    try {
      const r = await fetch(`${API}/api/admin/site-copy/runs`, { headers: authHeader() });
      const data = await r.json();
      setRuns(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    finally { setLoadingRuns(false); }
  };

  const toggleDest = (id) => {
    const s = String(id);
    setDestSites(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
    setPreview(null);
    setCostExclusions({});
  };

  const filteredDests = useMemo(() => {
    const term = destSearch.trim().toLowerCase();
    return sites
      .filter(s => String(s.id) !== sourceSite)
      .filter(s => !term || s.name.toLowerCase().includes(term)
        || (s.client_name || '').toLowerCase().includes(term));
  }, [sites, sourceSite, destSearch]);

  const handlePreview = async () => {
    if (sourceSite === '__none__') { toast.error('Choose a source site'); return; }
    if (destSites.length === 0) { toast.error('Choose at least one destination site'); return; }
    setLoading(true);
    try {
      const body = {
        source_site_id: parseInt(sourceSite),
        dest_site_ids: destSites.map(Number),
        dry_run: true,
        include_measures: includeMeasures,
        include_prices: includePrices,
        overwrite_measures: overwriteMeasures,
        overwrite_prices: overwritePrices,
        include_costs: includeCosts,
        overwrite_costs: overwriteCosts,
        cost_dest_session_ids: includeCosts ? sessionIdMap() : {},
      };
      const r = await fetch(`${API}/api/admin/site-copy/preview`, {
        method: 'POST', headers: authHeader(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Preview failed'); return; }
      setPreview(d);
      setApplyResult(null);
      const initAssign = {};
      (d.per_dest || []).forEach(dest => {
        initAssign[String(dest.dest_site_id)] = new Set((dest.additions || []).map(x => x.product_id));
      });
      setAssignSel(initAssign);
      setCostSel({});
    } catch { toast.error('Preview failed'); }
    finally { setLoading(false); }
  };

  const handleApply = async () => {
    setShowApplyConfirm(false);
    setApplying(true);
    try {
      const toLists = (m) => Object.fromEntries(destSites.map(id => [id, [...(m[id] || [])]]));
      const body = {
        source_site_id: parseInt(sourceSite),
        dest_site_ids: destSites.map(Number),
        dry_run: false,
        notes: `UI apply — source site ${sourceSite}`,
        include_measures: includeMeasures,
        include_prices: includePrices,
        overwrite_measures: overwriteMeasures,
        overwrite_prices: overwritePrices,
        include_costs: includeCosts,
        overwrite_costs: overwriteCosts,
        assignment_product_ids: toLists(assignSel),
        cost_dest_session_ids: includeCosts ? sessionIdMap() : {},
        cost_product_ids: includeCosts ? toLists(costSel) : {},
      };
      const r = await fetch(`${API}/api/admin/site-copy/apply`, {
        method: 'POST', headers: authHeader(), body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = typeof d.detail === 'string' ? d.detail : `Apply failed (HTTP ${r.status}) — nothing was saved.`;
        toast.error(msg);
        return;
      }
      setApplyResult(d);
      toast.success(`Saved — run ${String(d.run_id).slice(0, 8)}`);
      setPreview(null);
      setAssignSel({});
      setCostSel({});
      loadRuns();
      loadDestSessions();
    } catch { toast.error('Apply failed — no response from the server. Nothing is confirmed as saved; check Copy Audit History.'); }
    finally { setApplying(false); }
  };

  const selAssignCount = Object.values(assignSel).reduce((n, st) => n + st.size, 0);
  const selCostCount = Object.values(costSel).reduce((n, st) => n + st.size, 0);
  const selCostRows = (preview?.per_dest || []).flatMap(d =>
    (d.costs?.items || []).filter(i => costSel[String(d.dest_site_id)]?.has(i.product_id))
      .map(i => ({ ...i, dest_site_name: d.dest_site_name })));
  const hasChanges = preview
    ? (selAssignCount > 0
       || (includeMeasures && preview.per_dest?.some(d => d.measures?.count_to_copy > 0 || (overwriteMeasures && d.measures?.count_conflicts > 0)))
       || (includePrices && preview.per_dest?.some(d => d.prices?.count_to_copy > 0 || (overwritePrices && d.prices?.count_conflicts > 0)))
       || (includeCosts && selCostCount > 0))
    : false;

  const opts = { include_measures: includeMeasures, include_prices: includePrices, overwrite_measures: overwriteMeasures, overwrite_prices: overwritePrices, include_costs: includeCosts, overwrite_costs: overwriteCosts, source_site_name: preview?.source_site_name };

  return (
    <div className="space-y-6 p-6 max-w-[1200px] mx-auto" data-testid="copy-products-page">

      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => navigate('/stocktaking/site-settings')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors shrink-0"
          data-testid="copy-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex-1 min-w-[220px]">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Copy className="h-6 w-6" /> Copy Assigned Products
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Copy product assignments, measure overrides, bar prices and opening-stock costs from one site to another.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadRuns} data-testid="copy-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh History
        </Button>
      </div>

      {/* Isolation note */}
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
        <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">Assignment copy only by default</p>
          <p className="text-amber-700 text-xs mt-0.5">
            This tool adds missing product assignments to destination sites.
            Measure overrides, bar prices and opening-stock costs are only copied when explicitly enabled below.
            Existing destination overrides and prices are preserved unless overwrite is selected.
            Stock quantities, postmix overrides and audit history are never affected.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">

        {/* ── Config panel ──────────────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 p-4 space-y-4 bg-white">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Configuration</p>

            {/* Source site — searchable combobox */}
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Source site</label>
              <SiteCombobox
                sites={sites}
                value={sourceSite}
                onValueChange={v => { setSourceSite(v); setPreview(null); setDestSites([]); }}
                placeholder="Search by client or site name…"
                testId="copy-source-trigger"
              />
            </div>

            {/* Copy options */}
            <div className="space-y-3 border-t border-slate-100 pt-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Copy Options</p>

              {/* Include measures */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <input
                    id="inc-measures"
                    type="checkbox"
                    checked={includeMeasures}
                    onChange={e => { setIncludeMeasures(e.target.checked); setPreview(null); if (!e.target.checked) setOverwriteMeasures(false); }}
                    className="rounded"
                    data-testid="include-measures-checkbox"
                  />
                  <label htmlFor="inc-measures" className="text-sm text-slate-700 cursor-pointer">
                    Include site measure overrides
                  </label>
                </div>
                {includeMeasures && (
                  <div className="ml-5 flex items-center gap-2">
                    <input
                      id="overwrite-measures"
                      type="checkbox"
                      checked={overwriteMeasures}
                      onChange={e => { setOverwriteMeasures(e.target.checked); setPreview(null); }}
                      className="rounded"
                      data-testid="overwrite-measures-checkbox"
                    />
                    <label htmlFor="overwrite-measures" className="text-xs text-amber-700 cursor-pointer">
                      Overwrite existing destination measure overrides (conflicts)
                    </label>
                  </div>
                )}
              </div>

              {/* Include bar prices */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <input
                    id="inc-prices"
                    type="checkbox"
                    checked={includePrices}
                    onChange={e => { setIncludePrices(e.target.checked); setPreview(null); if (!e.target.checked) setOverwritePrices(false); }}
                    className="rounded"
                    data-testid="include-prices-checkbox"
                  />
                  <label htmlFor="inc-prices" className="text-sm text-slate-700 cursor-pointer">
                    Include site bar prices
                  </label>
                </div>
                {includePrices && (
                  <div className="ml-5 space-y-1">
                    <div className="flex items-center gap-2">
                      <input
                        id="overwrite-prices"
                        type="checkbox"
                        checked={overwritePrices}
                        onChange={e => { setOverwritePrices(e.target.checked); setPreview(null); }}
                        className="rounded"
                        data-testid="overwrite-prices-checkbox"
                      />
                      <label htmlFor="overwrite-prices" className="text-xs text-amber-700 cursor-pointer">
                        Overwrite existing destination prices (creates new effective-today row)
                      </label>
                    </div>
                    <p className="text-xs text-slate-400 ml-0.5">
                      Prices are blocked automatically when source and destination have different currencies.
                    </p>
                  </div>
                )}
              </div>

              {/* Include opening-stock costs */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <input
                    id="inc-costs"
                    type="checkbox"
                    checked={includeCosts}
                    onChange={e => { setIncludeCosts(e.target.checked); setPreview(null); setCostExclusions({}); if (!e.target.checked) setOverwriteCosts(false); }}
                    className="rounded"
                    data-testid="include-costs-checkbox"
                  />
                  <label htmlFor="inc-costs" className="text-sm text-slate-700 cursor-pointer">
                    Copy opening-stock costs
                  </label>
                </div>
                {includeCosts && (
                  <div className="ml-5 space-y-1">
                    <p className="text-xs text-slate-500">
                      Writes the source site's cost (last completed session, else latest posted delivery)
                      into the Opening Stock of a destination session you choose, per unit and only for
                      rows you tick. Rows counted in a different unit are never converted.
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        id="overwrite-costs"
                        type="checkbox"
                        checked={overwriteCosts}
                        onChange={e => { setOverwriteCosts(e.target.checked); setPreview(null); setCostExclusions({}); }}
                        className="rounded"
                        data-testid="overwrite-costs-checkbox"
                      />
                      <label htmlFor="overwrite-costs" className="text-xs text-amber-700 cursor-pointer">
                        Overwrite destination costs that already have a value
                      </label>
                    </div>
                    <p className="text-xs text-slate-400">
                      Only the opening value changes. Quantities, units, delivery history and costing method are not changed.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Destination sites */}
            {sourceSite !== '__none__' && (
              <div className="border-t border-slate-100 pt-3">
                <label className="text-xs text-slate-500 mb-1 block">
                  Destination site(s) — {destSites.length} selected
                </label>
                {/* Select All / Deselect All */}
                <div className="flex gap-2 mb-1.5">
                  <button
                    type="button"
                    data-testid="copy-dest-select-all"
                    className="flex items-center gap-1 text-xs text-violet-700 hover:text-violet-900 transition-colors"
                    onClick={() => setDestSites(
                      filteredDests
                        .filter(s => String(s.id) !== sourceSite)
                        .map(s => String(s.id))
                    )}
                  >
                    <CheckSquare className="h-3.5 w-3.5" />
                    Select all{destSearch ? ' filtered' : ''}
                  </button>
                  <span className="text-slate-300">|</span>
                  <button
                    type="button"
                    data-testid="copy-dest-deselect-all"
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                    onClick={() => setDestSites([])}
                  >
                    <Square className="h-3.5 w-3.5" />
                    Deselect all
                  </button>
                </div>
                {/* Search filter */}
                <div className="relative mb-1.5">
                  <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                  <Input
                    value={destSearch}
                    onChange={e => setDestSearch(e.target.value)}
                    placeholder="Filter destinations…"
                    className="pl-7 h-7 text-xs"
                    data-testid="dest-search-input"
                  />
                  {destSearch && (
                    <button onClick={() => setDestSearch('')}
                      className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100"
                  data-testid="copy-dest-list">
                  {filteredDests.length === 0 && (
                    <p className="text-xs text-slate-400 px-3 py-3 text-center">No sites match.</p>
                  )}
                  {filteredDests.map(s => (
                    <label key={s.id}
                      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 transition-colors ${
                        destSites.includes(String(s.id)) ? 'bg-violet-50' : ''
                      }`}
                      data-testid={`copy-dest-${s.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={destSites.includes(String(s.id))}
                        onChange={() => toggleDest(s.id)}
                      />
                      <span className="flex-1 min-w-0">
                        {s.client_name && (
                          <span className="text-xs text-slate-400 block truncate">{s.client_name}</span>
                        )}
                        <span className="truncate">{s.name}</span>
                      </span>
                      <span className="text-xs text-slate-400 shrink-0">{s.product_count}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {includeCosts && (
              <DestSessionChooser
                sites={sites}
                destSites={destSites}
                destSessions={destSessions}
                value={costSessionIds}
                onChange={(id, v) => { setCostSessionIds(prev => ({ ...prev, [id]: v })); setPreview(null); setCostSel({}); }}
              />
            )}

            <Button
              data-testid="copy-preview-btn"
              className="w-full h-9"
              onClick={handlePreview}
              disabled={loading || sourceSite === '__none__' || destSites.length === 0}
            >
              {loading ? 'Loading preview…' : 'Preview Copy'}
            </Button>
          </div>
        </div>

        {/* ── Preview panel ─────────────────────────────────────────────── */}
        <div className="space-y-4">
          {applyResult && (
            <ApplyResultPanel result={applyResult} onClose={() => setApplyResult(null)} onExport={handleExport} />
          )}
          {!preview ? (
            <div className="rounded-lg border border-dashed border-slate-200 p-12 text-center text-slate-400 text-sm">
              Select a source and destination site(s), configure copy options, then click Preview Copy.
            </div>
          ) : (
            <div className="space-y-4" data-testid="copy-preview-panel">
              {/* Summary row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-center">
                  <p className="text-3xl font-bold text-green-700" data-testid="summary-assign-selected">{selAssignCount}</p>
                  <p className="text-xs text-green-600 mt-1">Assignments selected (of {preview.total_added} missing)</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
                  <p className="text-3xl font-bold text-slate-500">{preview.total_skipped}</p>
                  <p className="text-xs text-slate-500 mt-1">Already assigned</p>
                </div>
                {includeMeasures && (
                  <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-center">
                    <p className="text-3xl font-bold text-sky-700">
                      {preview.per_dest?.reduce((s, d) => s + (d.measures?.count_to_copy || 0) + (overwriteMeasures ? (d.measures?.count_conflicts || 0) : 0), 0) ?? 0}
                    </p>
                    <p className="text-xs text-sky-600 mt-1">Measures to copy</p>
                  </div>
                )}
                {includePrices && (
                  <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 text-center">
                    <p className="text-3xl font-bold text-violet-700">
                      {preview.per_dest?.reduce((s, d) => s + (d.prices?.count_to_copy || 0) + (overwritePrices ? (d.prices?.count_conflicts || 0) : 0), 0) ?? 0}
                    </p>
                    <p className="text-xs text-violet-600 mt-1">Prices to copy</p>
                  </div>
                )}
                {includeCosts && (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-center">
                    <p className="text-3xl font-bold text-emerald-700" data-testid="summary-cost-selected">{selCostCount}</p>
                    <p className="text-xs text-emerald-600 mt-1">Opening costs selected</p>
                  </div>
                )}
              </div>

              {/* Currency warnings */}
              {includePrices && preview.per_dest?.some(d => d.prices?.count_blocked > 0) && (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <strong>Currency mismatch detected.</strong> Prices cannot be copied to destinations
                    with a different currency. Affected destinations are highlighted below.
                  </div>
                </div>
              )}

              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                {preview.isolation_note}
              </div>

              {/* Per-destination breakdown */}
              {preview.per_dest?.map(d => (
                <DestPreviewCard
                  key={d.dest_site_id}
                  d={d}
                  opts={opts}
                  assignSel={assignSel[String(d.dest_site_id)] || new Set()}
                  onAssign={(pid, ids) => updateAssign(d.dest_site_id, pid, ids)}
                  costSel={costSel[String(d.dest_site_id)] || new Set()}
                  onCost={(pid, ids) => updateCost(d.dest_site_id, pid, ids)}
                />
              ))}

              {/* Apply / no-changes */}
              {hasChanges ? (
                <>
                  <Button
                    data-testid="copy-apply-btn"
                    className="bg-slate-900 text-white hover:bg-slate-700 h-10 px-6"
                    onClick={() => setShowApplyConfirm(true)}
                    disabled={applying}
                  >
                    {applying ? 'Applying…' : 'Apply Copy'}
                  </Button>
                  <AlertDialog open={showApplyConfirm} onOpenChange={setShowApplyConfirm}>
                    <AlertDialogContent className="max-w-2xl" data-testid="copy-apply-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Apply these changes?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="space-y-2 text-sm text-slate-600">
                            <p>{selAssignCount} product assignment{selAssignCount !== 1 ? 's' : ''} will be added across {destSites.length} destination{destSites.length !== 1 ? 's' : ''}.</p>
                            {(includeMeasures || includePrices) && (
                              <p>Measures/prices: as shown in the preview{(overwriteMeasures || overwritePrices) ? ' — existing values may be overwritten' : ''}.</p>
                            )}
                            {includeCosts && (
                              <div data-testid="copy-confirm-cost-list">
                                <p className="font-medium text-slate-800">{selCostRows.length} opening-stock cost{selCostRows.length !== 1 ? 's' : ''} will be written:</p>
                                <ul className="max-h-48 overflow-y-auto text-xs space-y-0.5 mt-1">
                                  {selCostRows.map(i => (
                                    <li key={`${i.dest_site_name}-${i.product_id}`} className={i.status === 'conflict' ? 'text-red-700' : ''}>
                                      {i.dest_site_name} · {i.product_name}: {i.dest.quantity} {i.dest.unit_type || i.dest.unit_label} — £{Number(i.dest.value || 0).toFixed(2)} → £{i.after.value.toFixed(2)}
                                      {i.status === 'conflict' && ' (OVERWRITES an existing cost)'}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <p className="text-xs text-slate-500">Every change is logged in Copy Audit History with before/after values. It is not undone automatically.</p>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="copy-apply-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="copy-apply-confirm-btn" onClick={handleApply}
                          className="bg-red-700 hover:bg-red-800">
                          Yes, apply now
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded p-3">
                  <CheckCircle2 className="h-4 w-4" />
                  Everything is already up to date — no changes needed.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Run history */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" data-testid="copy-runs-table">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">Copy Audit History</h3>
        </div>
        {loadingRuns ? (
          <p className="p-4 text-center text-slate-400 text-sm">Loading…</p>
        ) : runs.length === 0 ? (
          <p className="p-4 text-center text-slate-400 text-sm">No copy runs yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-semibold text-slate-500">Run</th>
                <th className="text-left p-3 font-semibold text-slate-500">Actor</th>
                <th className="text-left p-3 font-semibold text-slate-500">Source</th>
                <th className="text-left p-3 font-semibold text-slate-500">Dests</th>
                <th className="text-left p-3 font-semibold text-slate-500">Assigned</th>
                <th className="text-left p-3 font-semibold text-slate-500">Existing</th>
                <th className="text-left p-3 font-semibold text-slate-500">Measures</th>
                <th className="text-left p-3 font-semibold text-slate-500">Prices</th>
                <th className="text-left p-3 font-semibold text-slate-500">Opening costs</th>
                <th className="text-left p-3 font-semibold text-slate-500">When</th>
                <th className="text-left p-3 font-semibold text-slate-500">Export</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(run => (
                <tr key={run.run_id} className="border-b border-slate-100" data-testid={`copy-run-${run.run_id.slice(0, 8)}`}>
                  <td className="p-3 font-mono">{run.run_id.slice(0, 12)}…</td>
                  <td className="p-3 text-slate-600">{run.actor_name || '—'}</td>
                  <td className="p-3">
                    {sites.find(s => s.id === run.source_site_id)?.name || `#${run.source_site_id}`}
                  </td>
                  <td className="p-3">
                    {(run.dest_site_ids || []).length} site{(run.dest_site_ids || []).length !== 1 ? 's' : ''}
                  </td>
                  <td className="p-3 font-semibold text-green-700">+{run.total_added}</td>
                  <td className="p-3 text-slate-500">{run.total_skipped ?? '—'}</td>
                  <td className="p-3 text-sky-700">
                    {(run.measures_added > 0 || run.measures_updated > 0)
                      ? `+${run.measures_added}${run.measures_updated > 0 ? ` /${run.measures_updated}↑` : ''}`
                      : '—'}
                  </td>
                  <td className="p-3 text-violet-700">
                    {(run.prices_added > 0 || run.prices_updated > 0)
                      ? `+${run.prices_added}${run.prices_updated > 0 ? ` /${run.prices_updated}↑` : ''}`
                      : run.currency_blocked > 0
                      ? <span className="text-red-500">{run.currency_blocked} blocked</span>
                      : '—'}
                  </td>
                  <td className="p-3 text-emerald-700" data-testid={`copy-run-costs-${run.run_id.slice(0, 8)}`}>
                    {(run.costs_added > 0 || run.costs_overwritten > 0)
                      ? `+${run.costs_added}${run.costs_overwritten > 0 ? ` /${run.costs_overwritten}↑` : ''}`
                      : '—'}
                  </td>
                  <td className="p-3 text-slate-400">{run.created_at?.slice(0, 19)?.replace('T', ' ')}</td>
                  <td className="p-3">
                    <button
                      data-testid={`copy-export-${run.run_id.slice(0, 8)}`}
                      onClick={() => handleExport(run.run_id)}
                      className="flex items-center gap-1 text-slate-500 hover:text-slate-800 cursor-pointer"
                    >
                      <Download className="h-3.5 w-3.5" /> CSV
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
